/**
 * REST-клиент онбординга (/api/v1/onboarding*, /api/v1/admin/onboarding*).
 */

import { del, get, patch, post, put } from "@/lib/api-client-v2";

export type OnboardingStepDto = {
  order: number;
  element_selector?: string | null;
  placement?: string;
  title?: string | null;
  content?: string | null;
  wait_for_selector?: string | null;
  is_skippable?: boolean;
  media_url?: string | null;
};

export type TourStateResponse = {
  enabled: boolean;
  tour_key?: string | null;
  tour_name?: string | null;
  current_step?: OnboardingStepDto | null;
  total_steps: number;
  is_completed: boolean;
  current_step_order: number;
  steps: OnboardingStepDto[];
};

export type StepCompleteResponse = {
  tour_key: string;
  current_step_order: number;
  is_finished: boolean;
  next_step?: OnboardingStepDto | null;
  status: string;
};

export type TourActionResponse = {
  tour_key: string;
  status: string;
  current_step_order: number;
};

export type OnboardingTourOut = {
  id: number;
  key: string;
  name: string;
  description?: string | null;
  is_active: boolean;
  is_default: boolean;
  trigger_rules: Record<string, unknown>;
  version: number;
  steps_count: number;
  steps: OnboardingStepDto[];
  created_at?: string | null;
  updated_at?: string | null;
};

export type OnboardingSettingsOut = {
  global_switch: boolean;
  show_for_existing: boolean;
  updated_at?: string | null;
  updated_by?: string | null;
};

export type OnboardingStepCreate = {
  step_order: number;
  element_selector?: string | null;
  placement?: string;
  title?: string | null;
  content?: string | null;
  media_url?: string | null;
  wait_for_selector?: string | null;
  is_skippable?: boolean;
};

export const onboardingApi = {
  getState: () => get<TourStateResponse>("/onboarding/state"),

  completeStep: (tourKey: string) =>
    post<StepCompleteResponse>(`/onboarding/step/${encodeURIComponent(tourKey)}/complete`),

  dismissTour: (tourKey: string) =>
    post<TourActionResponse>(`/onboarding/tour/${encodeURIComponent(tourKey)}/dismiss`),

  resetTour: (tourKey: string) =>
    post<TourActionResponse>(`/onboarding/tour/${encodeURIComponent(tourKey)}/reset`),

  adminListTours: () =>
    get<{ items: OnboardingTourOut[]; total: number }>("/admin/onboarding/tours"),

  adminCreateTour: (body: {
    key: string;
    name: string;
    description?: string | null;
    is_active?: boolean;
    is_default?: boolean;
    trigger_rules?: Record<string, unknown>;
    steps?: OnboardingStepCreate[];
  }) => post<OnboardingTourOut>("/admin/onboarding/tours", body),

  adminUpdateTour: (
    id: number,
    body: {
      name?: string;
      description?: string | null;
      is_active?: boolean;
      is_default?: boolean;
      trigger_rules?: Record<string, unknown>;
      steps?: OnboardingStepCreate[];
    },
  ) => put<OnboardingTourOut>(`/admin/onboarding/tours/${id}`, body),

  adminToggleTour: (id: number) =>
    patch<OnboardingTourOut>(`/admin/onboarding/tours/${id}/toggle`),

  adminDeleteTour: (id: number) => del<void>(`/admin/onboarding/tours/${id}`),

  adminGetSettings: () => get<OnboardingSettingsOut>("/admin/onboarding/settings"),

  adminPutSettings: (body: { global_switch?: boolean; show_for_existing?: boolean }) =>
    put<OnboardingSettingsOut>("/admin/onboarding/settings", body),
};
